import React, { useEffect, useState, useCallback, useRef, ReactNode } from 'react';

interface InfiniteHorizontalScrollProps {
  children: ReactNode[];
  className?: string;
  itemClassName?: string;
  showArrows?: boolean;
  /** When this changes (e.g. startup removed from row), scroll resets so loop clones stay in sync */
  contentKey?: string;
  /** Loop as soon as the row overflows. Default requires 1.5× width so short lists do not repeat. */
  loopWhenOverflow?: boolean;
}

/**
 * Componente de scroll horizontal infinito que solo duplica elementos cuando es necesario.
 * Si el contenido no hace overflow, muestra los elementos sin duplicar.
 * Si hace overflow, usa solo 2 copias (una antes y una después) para crear el efecto infinito.
 */
const InfiniteHorizontalScroll: React.FC<InfiniteHorizontalScrollProps> = ({
  children,
  className = '',
  itemClassName = '',
  showArrows = true,
  contentKey,
  loopWhenOverflow = false,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const innerContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [needsInfiniteScroll, setNeedsInfiniteScroll] = useState(false);
  const isScrolling = useRef(false);

  // Check if content overflows the visible area (determines arrow visibility)
  const checkIfOverflows = useCallback(() => {
    const container = scrollContainerRef.current;
    const inner = innerContainerRef.current;
    if (!container || !inner || children.length === 0) return false;

    return inner.scrollWidth > container.clientWidth;
  }, [children.length]);

  // Check if there's enough content to loop without items immediately repeating.
  // Requires content to be ≥ 1.5× the viewport so at least half a screen of new
  // content exists before the loop wraps — prevents obvious repetition for short lists.
  const checkIfNeedsLoop = useCallback(() => {
    const container = scrollContainerRef.current;
    const inner = innerContainerRef.current;
    if (!container || !inner || children.length === 0) return false;

    const minWidth = container.clientWidth * (loopWhenOverflow ? 1 : 1.5);
    return inner.scrollWidth > minWidth;
  }, [children.length, loopWhenOverflow]);

  // Check scroll position and update arrow visibility
  const checkScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isScrolling.current) return;

    const overflows = checkIfOverflows();
    setNeedsInfiniteScroll(checkIfNeedsLoop());

    if (!overflows) {
      setShowLeftArrow(false);
      setShowRightArrow(false);
      return;
    }

    // Show arrows if content overflows
    setShowLeftArrow(true);
    setShowRightArrow(true);
  }, [checkIfOverflows, checkIfNeedsLoop]);

  // Calculate original content width
  const getOriginalContentWidth = useCallback(() => {
    const inner = innerContainerRef.current;
    if (!inner || children.length === 0) return 0;

    const firstItem = inner.children[0] as HTMLElement;
    if (!firstItem) return 0;

    // When 3 sets are rendered, Set 2 starts at index children.length — measure directly.
    const set2First = inner.children[children.length] as HTMLElement;
    if (set2First) {
      return set2First.offsetLeft - firstItem.offsetLeft;
    }

    // Fallback for single-set layout (before tripling): derive gap from item spacing.
    const lastItem = inner.children[children.length - 1] as HTMLElement;
    if (!lastItem) return 0;
    let gapPx = 12;
    if (children.length > 1) {
      const secondItem = inner.children[1] as HTMLElement;
      if (secondItem) {
        gapPx = secondItem.offsetLeft - (firstItem.offsetLeft + firstItem.offsetWidth);
      }
    }
    return lastItem.offsetLeft + lastItem.offsetWidth + gapPx - firstItem.offsetLeft;
  }, [children.length]);

  // Handle infinite scroll loop
  const handleScroll = useCallback(() => {
    if (!needsInfiniteScroll) return;

    const container = scrollContainerRef.current;
    if (!container || isScrolling.current) return;

    const { scrollLeft } = container;
    const originalContentWidth = getOriginalContentWidth();

    if (originalContentWidth === 0) return;

    // Structure: [Set1-duplicated] [Set2-original] [Set3-duplicated]
    // When at the end of Set3, jump to start of Set2 (original)
    if (scrollLeft >= originalContentWidth * 2 - 10) {
      isScrolling.current = true;
      container.scrollLeft = originalContentWidth;
      setTimeout(() => {
        isScrolling.current = false;
      }, 50);
    }
    // When at the start of Set1, jump to end of Set2 (original)
    else if (scrollLeft <= originalContentWidth - 10) {
      isScrolling.current = true;
      container.scrollLeft = originalContentWidth * 2 - container.clientWidth;
      setTimeout(() => {
        isScrolling.current = false;
      }, 50);
    }
  }, [needsInfiniteScroll, getOriginalContentWidth]);

  // Scroll functions
  const scrollLeft = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth * 0.8;
    container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
  }, []);

  const scrollRight = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const scrollAmount = container.clientWidth * 0.8;
    container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
  }, []);

  // Initialize scroll position and check overflow (also when row content changes)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    setNeedsInfiniteScroll(false);
    container.scrollLeft = 0;

    // Wait for layout to calculate
    const timer = setTimeout(() => {
      const needsLoop = checkIfNeedsLoop();
      setNeedsInfiniteScroll(needsLoop);

      if (needsLoop) {
        // Defer scrollLeft assignment to the next macrotask so React re-renders
        // with 3 sets in the DOM first; getOriginalContentWidth then measures directly.
        setTimeout(() => {
          const originalContentWidth = getOriginalContentWidth();
          if (originalContentWidth > 0) {
            container.scrollLeft = originalContentWidth;
          }
        }, 0);
      }
      checkScrollPosition();
    }, 100);

    return () => clearTimeout(timer);
  }, [children.length, contentKey, checkIfNeedsLoop, checkScrollPosition, getOriginalContentWidth]);

  // Listen to scroll events
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    container.addEventListener('scroll', checkScrollPosition);
    window.addEventListener('resize', checkScrollPosition);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('scroll', checkScrollPosition);
      window.removeEventListener('resize', checkScrollPosition);
    };
  }, [handleScroll, checkScrollPosition]);

  const cloneChildForSet = useCallback((child: ReactNode, set: number, idx: number) => {
    if (React.isValidElement(child)) {
      return React.cloneElement(child, {
        key: `loop-${set}-${String(child.key ?? idx)}`,
      });
    }
    return child;
  }, []);

  // Prepare items to render
  // Only duplicate if infinite scroll is needed
  const itemsToRender = needsInfiniteScroll
    ? [
        ...children.map((child, idx) => ({ child, set: 0, idx })),
        ...children.map((child, idx) => ({ child, set: 1, idx })),
        ...children.map((child, idx) => ({ child, set: 2, idx })),
      ] // 3 copies for infinite scroll
    : children.map((child, idx) => ({ child, set: 0, idx })); // No duplication if no overflow

  return (
    <div className={`relative ${className}`}>
      {/* Left Arrow */}
      {showArrows && showLeftArrow && (
        <button
          onClick={scrollLeft}
          className="absolute left-0 top-0 bottom-6 z-10 w-12 sm:w-16 flex items-center justify-center transition-colors group"
          aria-label="Scroll left"
        >
          <svg
            className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400 group-hover:text-white transition-colors drop-shadow-md"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
      )}

      {/* Scrollable Container */}
      <div
        ref={scrollContainerRef}
        className="overflow-x-auto pb-4 startup-row-scroll"
        style={{
          scrollbarWidth: 'none', // Firefox
          msOverflowStyle: 'none', // IE/Edge
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div ref={innerContainerRef} className="flex space-x-3 sm:space-x-4 px-3 sm:px-4 lg:px-6">
          {itemsToRender.map((item, index) => (
            <div
              key={needsInfiniteScroll ? `item-${item.set}-${item.idx}` : `item-${item.idx}`}
              className={`flex-shrink-0 ${itemClassName}`}
            >
              {cloneChildForSet(item.child, item.set, item.idx)}
            </div>
          ))}
        </div>
      </div>

      {/* Right Arrow */}
      {showArrows && showRightArrow && (
        <button
          onClick={scrollRight}
          className="absolute right-0 top-0 bottom-6 z-10 w-12 sm:w-16 flex items-center justify-center transition-colors group"
          aria-label="Scroll right"
        >
          <svg
            className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400 group-hover:text-white transition-colors drop-shadow-md"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default InfiniteHorizontalScroll;
